<template>
  <div>
    <b-modal centered id="modal-video" title="Gerar código vídeo" @hide="resetModal">
      <div class="modal-body-content">
        <!-- Seleção de Provedor -->
        <div class="form-group">
          <label for="provider-select">Escolha Provedor:</label>
          <b-form-select 
            id="provider-select"
            placeholder="Escolha um provedor" 
            v-model="selectedProviderId" 
            :options="providerOptions" 
            @change="findProvider">
          </b-form-select>
        </div>

        <!-- Aviso de Provedor -->
        <div v-if="!providers.length" class="alert alert-warning" role="alert">
          ⚠️ Nenhum provedor ativo encontrado. Ative um provedor para gerar o código do vídeo.
        </div>
        <div v-else-if="!selectedProviderId" class="alert alert-info" role="alert">
          ℹ️ Selecione um provedor ativo para gerar o código do vídeo.
        </div>

        <!-- Seleção de Arquivo -->
        <div class="form-group">
          <label for="video-input">Escolha o arquivo:</label>
          <b-form-file 
            id="video-input"
            @input="handleFileUpload" 
            placeholder="Escolha um arquivo de vídeo..." 
            drop-placeholder="Solte o arquivo aqui..."
            accept=".mp4,.3gp,video/mp4,video/3gpp">
          </b-form-file>
        </div>

        <!-- Informações do Arquivo -->
        <div v-if="fileData" class="alert alert-info" role="alert">
          <p><strong>Arquivo:</strong> {{ fileData.name }}</p>
          <p><strong>Tamanho:</strong> {{ formatFileSize(fileData.size) }}</p>
          <p><strong>Tipo:</strong> {{ fileData.type }}</p>
        </div>

        <!-- Mensagens de Validação -->
        <div v-if="validationError" class="alert alert-warning" role="alert">
          ⚠️ {{ validationError }}
        </div>

        <!-- Mensagens de Erro -->
        <div v-if="uploadError" class="alert alert-danger" role="alert">
          ❌ {{ uploadError }}
        </div>

        <!-- Mensagens de Sucesso -->
        <div v-if="uploadSuccess" class="alert alert-success" role="alert">
          ✅ Vídeo enviado com sucesso!
          <p class="mt-2"><strong>Código do Vídeo:</strong></p>
          <code class="d-block p-2 bg-light rounded">{{ videoId }}</code>
        </div>

        <!-- Loading -->
        <div v-if="isLoading" class="text-center">
          <b-spinner small></b-spinner>
          <p class="mt-2">Enviando vídeo...</p>
        </div>
      </div>

      <template v-slot:modal-footer>
        <div class="d-flex align-items-center" @click.capture="logClickAttempt">
          <b-button 
            variant="secondary" 
            @click="closeModal">
            Cancelar
          </b-button>
          <b-button 
            variant="success" 
            @click="onClickGerar"
            :disabled="isGerarDisabled"
            class="ml-2">
            {{ isLoading ? 'Enviando...' : 'Gerar Código Vídeo' }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import AtendimentoRepository from '@/shared/http/repositories/atendimento/atendimento'
import NumRepository from '@/shared/http/repositories/access/cadastroNum'

const MAX_VIDEO_SIZE = 16 * 1024 * 1024 // 16MB
const ACCEPTED_FORMATS = ['video/mp4', 'video/3gpp']

export default {
  name: 'ModalVideo',
  props: {
    hsmId: {
      type: [Number, String],
      default: null
    }
  },
  data () {
    return {
      fileData: null,
      providers: [],
      selectedProviderId: null,
      selectedProvider: null,
      isLoading: false,
      uploadError: '',
      validationError: '',
      uploadSuccess: false,
      videoId: ''
    }
  },
  watch: {
    selectedProviderId () {
      this.findProvider()
    }
  },
  async mounted () {
    await this.onGetProviders()
  },
  methods: {
    logClickAttempt () {
    },
    onClickGerar () {
      this.onGetIdVideo()
    },
    handleFileUpload (payload) {
      let file = null

      if (payload && payload.target && payload.target.files && payload.target.files.length > 0) {
        file = payload.target.files[0]
      } else if (payload && payload instanceof File) {
        file = payload
      } else if (payload && payload.length && payload[0] instanceof File) {
        file = payload[0]
      }

      if (file) {
        this.validationError = ''
        this.uploadError = ''
        this.uploadSuccess = false

        // Validar arquivo
        if (!this.validateVideo(file)) {
          this.fileData = null
          return
        }

        const reader = new FileReader()
        reader.onload = (e) => {
          const result = e.target && e.target.result ? e.target.result : ''
          this.fileData = {
            name: file.name,
            type: file.type,
            size: file.size,
            base64: result ? result.toString().split(',')[1] : ''
          }
        }
        reader.readAsDataURL(file)
      }
    },

    validateVideo (file) {
      // Validar formato
      if (!ACCEPTED_FORMATS.includes(file.type)) {
        this.validationError = 'Formato não suportado. Use MP4 ou 3GP.'
        return false
      }

      // Validar tamanho
      if (file.size > MAX_VIDEO_SIZE) {
        this.validationError = `Arquivo muito grande. Máximo é 16MB (seu arquivo: ${this.formatFileSize(file.size)})`
        return false
      }

      return true
    },

    formatFileSize (bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    },

    closeModal () {
      this.$bvModal.hide('modal-video')
    },

    resetModal () {
      this.fileData = null
      this.validationError = ''
      this.uploadError = ''
      this.uploadSuccess = false
      this.videoId = ''
    },

    async onGetProviders () {
      const filter = {
        active: 1
      }
      const data = await NumRepository.getEnvAll(filter)
      const response = data.data.content
      this.providers = response
    },

    async onGetIdVideo () {
      if (!this.fileData) {
        this.uploadError = 'Selecione um arquivo de vídeo.'
        return
      }
      if (!this.selectedProvider) {
        this.findProvider()
      }
      if (!this.selectedProvider) {
        this.uploadError = 'Selecione um provedor.'
        return
      }

      this.isLoading = true
      this.uploadError = ''

      try {
        // ✅ CORRIGIDO: Adicionar prefixo data URL e objeto api
        const identifierProvider = this.selectedProvider.number ||
          this.selectedProvider.apinumber ||
          this.selectedProvider.identifierProvider ||
          this.selectedProviderId

        const req = {
          identifierProvider,
          filename: this.fileData.name,
          // ✅ FIX 1: Adicionar prefixo data URL (data:video/mp4;base64,...)
          base64: `data:${this.fileData.type};base64,${this.fileData.base64}`,
          // ✅ FIX 2: Incluir objeto api com dados do provedor
          api: {
            urlAPI: this.selectedProvider.urlAPI,
            number: this.selectedProvider.number,
            identifierProvider,
            accessToken: this.selectedProvider.accessToken,
            phoneNumberId: this.selectedProvider.phoneNumberId
          }
        }

        const response = await AtendimentoRepository.GetVideoId(req)
        
        // ✅ FIX 3: Extrair resposta correta (backend retorna idVideo)
        const mediaId = response.data.idVideo || response.data.data

        if (!mediaId) {
          throw new Error('Resposta vazia do servidor')
        }

        this.videoId = mediaId
        this.uploadSuccess = true

        if (this.hsmId) {
          try {
            await AtendimentoRepository.UpdateHsmMedia(this.hsmId, {
              hsmVideoId: mediaId,
              hasVideo: true
            })
          } catch (updateError) {
            console.error('Falha ao atualizar HSM com videoId:', updateError)
          }
        }

        // Emitir evento com o ID do vídeo
        this.$emit('idVideo', mediaId)
        this.closeModal()
      } catch (error) {
        console.error('Erro ao enviar video:', error)
        
        this.uploadError = (error && error.response && error.response.data && error.response.data.message)
          ? error.response.data.message
          : 'Erro ao enviar vídeo. Tente novamente.'
      } finally {
        this.isLoading = false
      }
    },

    findProvider () {
      if (!this.selectedProviderId) {
        this.selectedProvider = null
        return
      }
      const selectedId = String(this.selectedProviderId)
      this.selectedProvider = this.providers.find(provider => {
        const candidates = [
          provider.id,
          provider.number,
          provider.apinumber,
          provider.identifierProvider,
          provider.metaNumber
        ]
        return candidates.some(value => value != null && String(value) === selectedId)
      }) || null
    }
  },

  computed: {
    isGerarDisabled () {
      return !this.fileData || !this.selectedProviderId || this.isLoading || !!this.validationError
    },
    providerOptions () {
      return this.providers.map(provider => ({
        value: provider.number || provider.apinumber || provider.identifierProvider || provider.id,
        text: provider.name
      }))
    }
  }
}
</script>

<style scoped>
.modal-body-content {
  padding: 10px 0;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  font-weight: 500;
  margin-bottom: 5px;
  display: block;
}

.alert {
  margin-bottom: 15px;
}

.alert p {
  margin-bottom: 5px;
}

code {
  word-break: break-all;
  font-family: 'Courier New', monospace;
}

.d-block {
  display: block;
}

.p-2 {
  padding: 0.5rem;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.rounded {
  border-radius: 0.25rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.text-center {
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/views/private/configurations/hsm/components/modal/ModalVideo.vue